'use client';

import { OPT_OUT_COOKIE_NAME } from '@lib/constants/oneTrust';
import {
  getFunctionalConsentStatus,
  getPerformanceConsentStatus,
  getTargetingConsentStatus,
} from '@lib/cookieConsent/cookieConsent';
import Cookie from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';
import { waitForElementToExist } from 'utils';

const ONETRUST_SDK_WRAPPER = '#onetrust-consent-sdk';

const consentChangeListeners = new Set<() => void>();
let consentChangeRegistration: Promise<void> | undefined;

/**
 * OneTrust dedupes OnConsentChanged callbacks by their source text, so a
 * callback registered per hook instance only ever reaches the first instance.
 * One fan-out callback is registered instead, notifying every subscriber.
 */
const subscribeToConsentChanges = (listener: () => void) => {
  consentChangeListeners.add(listener);
  consentChangeRegistration ??= waitForElementToExist(
    ONETRUST_SDK_WRAPPER
  ).then(() => {
    const oneTrust = (window as any).OneTrust;
    if (!oneTrust) {
      consentChangeRegistration = undefined;
      return;
    }
    oneTrust.OnConsentChanged(() => {
      consentChangeListeners.forEach((notify) => notify());
    });
  });

  return () => {
    consentChangeListeners.delete(listener);
  };
};

export const useCookieSettings = () => {
  const [consentCategoriesState, setConsentCategoriesState] = useState({
    functional: false,
    targeting: false,
    performance: false,
  });

  const updateStateFromCookie = () => {
    const optanonConsent = Cookie.get(OPT_OUT_COOKIE_NAME);
    const cookieConsent = decodeURIComponent(optanonConsent || '');

    setConsentCategoriesState({
      functional: getFunctionalConsentStatus(cookieConsent),
      targeting: getTargetingConsentStatus(cookieConsent),
      performance: getPerformanceConsentStatus(cookieConsent),
    });
  };

  useEffect(() => {
    const unsubscribe = subscribeToConsentChanges(updateStateFromCookie);
    updateStateFromCookie();
    return unsubscribe;
  }, []);

  const openCookieSettings = useCallback(() => {
    const oneTrust = (window as any).OneTrust;
    if (oneTrust) {
      oneTrust.ToggleInfoDisplay();
    }
  }, []);

  return {
    functionalCategoryEnabled: consentCategoriesState.functional,
    targetingCategoryEnabled: consentCategoriesState.targeting,
    performanceCategoryEnabled: consentCategoriesState.performance,
    hasCookieConsent:
      consentCategoriesState.targeting && consentCategoriesState.functional,
    openCookieSettings,
  };
};
